import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronRight } from 'lucide-react';
import { apiClient } from '../api';
import SeverityChip, { SEVERITY } from '../components/UI/SeverityChip';

function timeAgo(isoString) {
  if (!isoString) return 'Never';
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function riskBand(score) {
  if (score > 75) return 'crit';
  if (score > 50) return 'high';
  if (score > 25) return 'med';
  return 'low';
}

function sparklinePoints(values, w = 112, h = 36) {
  if (!values || values.length < 2) return `0,${h / 2} ${w},${h / 2}`;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  return values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - 4 - ((v - min) / range) * (h - 8);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}

function trendDelta(values) {
  if (!values || values.length < 2) return 0;
  return values[values.length - 1] - values[0];
}

const RANGE_MS = { '24h': 24 * 60 * 60 * 1000, '7d': 7 * 24 * 60 * 60 * 1000, '30d': 30 * 24 * 60 * 60 * 1000 };
function withinRange(dateStr, range) {
  if (!dateStr) return false;
  return Date.now() - new Date(dateStr).getTime() <= RANGE_MS[range];
}

const CHANGE_KIND = {
  install: 'package installed', upgrade: 'package upgraded', downgrade: 'package downgraded', remove: 'package removed',
  port_opened: 'port opened', port_closed: 'port closed', service_added: 'service started', service_removed: 'service stopped',
};
function changeDetail(c) {
  if (c.change_type === 'port_opened') return `${c.package_name}/${c.ecosystem || 'tcp'} listening`;
  if (c.change_type === 'port_closed') return `${c.package_name}/${c.ecosystem || 'tcp'}`;
  if (c.change_type === 'service_added' || c.change_type === 'service_removed') return c.package_name;
  if (c.change_type === 'remove') return `${c.package_name} ${c.old_version || ''}`.trim();
  if (c.change_type === 'install') return `${c.package_name} ${c.new_version || ''}`.trim();
  return `${c.old_version || '?'} → ${c.new_version || '?'}`;
}

const Panel = ({ children, className = '', span }) => (
  <section
    className={`box-sizing-border-box p-6 border rounded-[14px] flex flex-col ${span === 2 ? 'md:col-span-2' : ''} ${className}`}
    style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}
  >
    {children}
  </section>
);

const Kicker = ({ children, action }) => (
  <div className="flex items-center justify-between mb-5">
    <div className="flex items-baseline gap-3">
      <h2 className="font-mono text-[11.5px] font-medium tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>{children}</h2>
    </div>
    {action}
  </div>
);

const Link_ = ({ to, children, navigate }) => (
  <button onClick={() => navigate(to)} className="text-[13px] py-1.5 hover:opacity-80 transition-opacity" style={{ color: 'var(--ink-link)' }}>
    {children}
  </button>
);

const Dashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({ riskScore: 0, assets: 0, events: 0, activeCve: 0, activeIoc: 0 });
  const [riskDistribution, setRiskDistribution] = useState({ CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 });
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [agentHealth, setAgentHealth] = useState({ registered: 0, active: 0, discovered: 0, lastSeen: null });
  const [versionChanges, setVersionChanges] = useState([]);
  const [allServers, setAllServers] = useState([]);
  const [selectedRange, setSelectedRange] = useState('7d');
  const [history, setHistory] = useState({ risk: [], assets: [], events: [] });
  const [lastFetch, setLastFetch] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const fetchStats = async () => {
    try {
      const [dashboardRes, riskRes, alertsRes, serversRes, changesRes] = await Promise.allSettled([
        apiClient.get('/api/dashboard'),
        apiClient.get('/api/dashboard/risk'),
        apiClient.get('/api/alerts/unified'),
        apiClient.get('/api/servers'),
        apiClient.get('/api/changes'),
      ]);

      let nextAssets = 0, nextEvents = 0, nextActiveCve = 0, nextActiveIoc = 0;
      if (dashboardRes.status === 'fulfilled') {
        const { total_servers, active_ioc, active_cve } = dashboardRes.value.data;
        nextAssets = total_servers || 0;
        nextActiveCve = active_cve || 0;
        nextActiveIoc = active_ioc || 0;
        nextEvents = nextActiveIoc + nextActiveCve;
      }
      setStats(prev => ({ ...prev, assets: nextAssets, events: nextEvents, activeCve: nextActiveCve, activeIoc: nextActiveIoc }));

      let nextRiskScore = 0;
      let riskByServer = new Map();
      if (riskRes.status === 'fulfilled') {
        const servers = riskRes.value.data.servers || [];
        riskByServer = new Map(servers.map(s => [s.id, s.risk_score]));
        if (servers.length > 0) {
          nextRiskScore = Math.round(servers.reduce((sum, s) => sum + s.risk_score, 0) / servers.length);
          const dist = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
          servers.forEach(s => {
            if (s.risk_score > 75) dist.CRITICAL++;
            else if (s.risk_score > 50) dist.HIGH++;
            else if (s.risk_score > 25) dist.MEDIUM++;
            else dist.LOW++;
          });
          setRiskDistribution(dist);
          setStats(prev => ({ ...prev, riskScore: nextRiskScore }));
        }
      }

      if (alertsRes.status === 'fulfilled') {
        setRecentAlerts((alertsRes.value.data.alerts || []).slice(0, 30));
      }

      if (serversRes.status === 'fulfilled') {
        const servers = (serversRes.value.data.servers || []).map(s => ({ ...s, risk_score: riskByServer.get(s.id) ?? 0 }));
        setAllServers(servers);
        const now = Date.now();
        const registered = servers.filter(s => s.lifecycle_state !== 'DISCOVERED');
        const discovered = servers.filter(s => s.lifecycle_state === 'DISCOVERED');
        const active = registered.filter(s => s.last_seen && (now - new Date(s.last_seen).getTime()) < 15 * 60 * 1000);
        const latestSeen = registered.map(s => s.last_seen).filter(Boolean).sort().reverse()[0] || null;
        setAgentHealth({ registered: registered.length, active: active.length, discovered: discovered.length, lastSeen: latestSeen });
      }

      if (changesRes.status === 'fulfilled') {
        setVersionChanges((changesRes.value.data.changes || []).slice(0, 30));
      }

      setHistory(prev => ({
        risk: [...prev.risk, nextRiskScore].slice(-10),
        assets: [...prev.assets, nextAssets].slice(-10),
        events: [...prev.events, nextEvents].slice(-10),
      }));
      setLastFetch(Date.now());
    } catch { /* transient — next poll retries */ }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const getAlertSeverity = (alert) => {
    if (alert.type === 'cve') return alert.severity === 'CRITICAL' ? 'crit' : alert.severity === 'HIGH' ? 'high' : alert.severity === 'MEDIUM' ? 'med' : 'low';
    return alert.threat_level === 'high' ? 'high' : alert.threat_level === 'medium' ? 'med' : 'low';
  };
  const getAlertMessage = (alert) => alert.type === 'cve'
    ? `${alert.package_name} ${alert.version} — ${alert.cve_id}`
    : (alert.description || `${alert.ioc_type}: ${alert.ioc_value}`);
  const getAlertMeta = (alert) => alert.type === 'cve'
    ? `${alert.hostname || alert.server_id || 'unknown host'} · NVD`
    : `${alert.hostname || alert.server_id || 'unknown host'} · MISP`;

  const band = riskBand(stats.riskScore);
  const ringR = 70;
  const ringC = 2 * Math.PI * ringR;
  const ringDash = (Math.min(100, Math.max(0, stats.riskScore)) / 100) * ringC;
  const delta = trendDelta(history.risk);

  const drivers = [
    { label: 'Critical assets', value: riskDistribution.CRITICAL, k: 'crit' },
    { label: 'Active IoC alerts', value: stats.activeIoc, k: 'high' },
    { label: 'Open CVEs', value: stats.activeCve, k: 'med' },
  ];
  const maxDriver = Math.max(1, ...drivers.map(d => d.value));

  const needsAttention = [
    ...recentAlerts.filter(a => withinRange(a.detected_at, selectedRange)).map(a => ({
      key: `alert-${a.type}-${a.id}`, k: getAlertSeverity(a), title: getAlertMessage(a), meta: getAlertMeta(a),
      ts: a.detected_at, onClick: () => navigate('/security-alerts'),
    })),
    ...allServers.filter(s => s.risk_score > 75).map(s => ({
      key: `asset-${s.id}`, k: 'crit', title: `${s.hostname} — risk ${Math.round(s.risk_score)}`, meta: `${s.ip_address || 'unknown IP'} · asset`,
      ts: s.last_seen, onClick: () => navigate(`/assets/${s.id}`),
    })),
  ]
    .sort((a, b) => (['crit', 'high', 'med', 'low'].indexOf(a.k) - ['crit', 'high', 'med', 'low'].indexOf(b.k)) || (new Date(b.ts || 0) - new Date(a.ts || 0)))
    .slice(0, 5);

  const totalAssetsForBands = stats.assets || 1;
  const bands = [
    { k: 'crit', label: 'Critical', value: riskDistribution.CRITICAL },
    { k: 'high', label: 'High', value: riskDistribution.HIGH },
    { k: 'med', label: 'Medium', value: riskDistribution.MEDIUM },
    { k: 'low', label: 'Low', value: riskDistribution.LOW },
  ];
  const maxBand = Math.max(1, ...bands.map(b => b.value));

  const dominantRisk = riskDistribution.CRITICAL > 0 ? 'crit' : riskDistribution.HIGH > 0 ? 'high' : riskDistribution.MEDIUM > 0 ? 'med' : 'low';
  const status = [
    { label: 'Registered agents', value: String(agentHealth.registered), dot: 'var(--ink-faint)' },
    { label: 'Active (15 min)', value: String(agentHealth.active), dot: agentHealth.active > 0 ? SEVERITY.good.fg : 'var(--ink-faint)' },
    { label: 'Discovered', value: String(agentHealth.discovered), dot: 'var(--ink-faint)' },
    { label: 'Total assets', value: String(stats.assets), dot: 'var(--ink-faint)' },
    { label: 'Open CVEs', value: String(stats.activeCve), dot: stats.activeCve > 0 ? SEVERITY.med.fg : 'var(--ink-faint)' },
    { label: 'IoC alerts', value: String(stats.activeIoc), dot: stats.activeIoc > 0 ? SEVERITY.high.fg : 'var(--ink-faint)' },
    { label: 'Last heartbeat', value: timeAgo(agentHealth.lastSeen), dot: SEVERITY.good.fg },
    { label: 'Dominant risk', value: SEVERITY[dominantRisk].label.charAt(0) + SEVERITY[dominantRisk].label.slice(1).toLowerCase(), dot: SEVERITY[dominantRisk].fg },
  ];

  const serverMap = new Map(allServers.map(s => [s.id, s.hostname]));
  const visibleChanges = versionChanges
    .filter(c => withinRange(c.detected_at, selectedRange))
    .slice(0, 6)
    .map(c => ({
      time: c.detected_at ? new Date(c.detected_at).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '--:--',
      host: serverMap.get(c.server_id) || c.server_id || 'unknown',
      kind: CHANGE_KIND[c.change_type] || c.change_type,
      detail: changeDetail(c),
    }));

  const secondsSinceRefresh = lastFetch ? Math.max(0, Math.floor((nowTick - lastFetch) / 1000)) : null;

  return (
    <div className="min-h-full box-border p-8 flex flex-col gap-6" style={{ background: 'var(--surf-bg)' }}>

        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-2">
            <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--accent)' }}>OPERATIONS OVERVIEW</span>
            <h1 className="font-heading text-[30px] font-semibold leading-tight tracking-[-0.01em]" style={{ color: 'var(--ink-text)' }}>Dashboard</h1>
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
              Posture across {stats.assets} assets and {agentHealth.registered} agents
              {secondsSinceRefresh !== null && ` · updated ${secondsSinceRefresh}s ago`}
            </p>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
            {[{ id: '24h', label: '24H' }, { id: '7d', label: '7D' }, { id: '30d', label: '30D' }].map(opt => (
              <button
                key={opt.id}
                onClick={() => setSelectedRange(opt.id)}
                className="h-[34px] px-3.5 rounded-[7px] font-mono text-xs font-medium border transition-colors"
                style={{
                  background: selectedRange === opt.id ? 'var(--surf-raised)' : 'transparent',
                  borderColor: selectedRange === opt.id ? 'var(--surf-line)' : 'transparent',
                  color: selectedRange === opt.id ? 'var(--ink-text)' : 'var(--ink-faint)',
                }}
              >
                {opt.label}
              </button>
            ))}
            <span aria-hidden="true" className="w-px h-[18px] mx-1" style={{ background: 'var(--surf-line)' }} />
            <button
              type="button" disabled title="Custom range — coming soon"
              className="h-[34px] flex items-center gap-1.5 px-3 rounded-[7px] font-mono text-xs cursor-not-allowed"
              style={{ color: 'var(--ink-faint)' }}
            >
              <Calendar className="w-3.5 h-3.5" />
              Custom
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

          {/* Security Posture */}
          <Panel span={2}>
            <Kicker action={<SeverityChip level={band} label={`${SEVERITY[band].label} RISK`} />}>SECURITY POSTURE</Kicker>
            <div className="flex items-center gap-10 flex-wrap">
              <div className="relative w-[168px] h-[168px] shrink-0">
                <svg width="168" height="168" viewBox="0 0 168 168" role="img" aria-label={`Risk score ${stats.riskScore} out of 100`}>
                  <circle cx="84" cy="84" r={ringR} fill="none" stroke="var(--surf-raised)" strokeWidth="12" />
                  <circle cx="84" cy="84" r={ringR} fill="none" stroke={SEVERITY[band].fg} strokeWidth="12" strokeLinecap="round"
                    strokeDasharray={`${ringDash} ${ringC}`} transform="rotate(-90 84 84)" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-heading text-[46px] font-semibold leading-none" style={{ color: 'var(--ink-text)' }}>{stats.riskScore}</span>
                  <span className="font-mono text-[11px] mt-1" style={{ color: 'var(--ink-faint)' }}>RISK / 100</span>
                </div>
              </div>
              <div className="flex-grow flex flex-col gap-3.5 min-w-[240px]">
                {delta !== 0 && (
                  <div className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--ink-muted)' }}>
                    <ChevronRight className="w-4 h-4 rotate-90" style={{ color: delta < 0 ? SEVERITY.good.fg : SEVERITY.crit.fg, transform: delta < 0 ? 'rotate(90deg)' : 'rotate(-90deg)' }} />
                    {delta < 0 ? 'Down' : 'Up'} {Math.abs(delta)} points (trailing poll window)
                  </div>
                )}
                <span className="font-mono text-[10.5px] tracking-[0.12em] mt-1" style={{ color: 'var(--ink-faint)' }}>WHAT'S DRIVING THIS</span>
                <div className="flex flex-col gap-1">
                  {drivers.map(d => (
                    <button key={d.label} onClick={() => navigate(d.k === 'crit' ? '/network-map' : '/security-alerts')} className="flex items-center gap-3.5 h-8 text-left">
                      <span className="w-[150px] text-sm shrink-0" style={{ color: 'var(--ink-text)' }}>{d.label}</span>
                      <span className="flex-grow h-2 rounded-full" style={{ background: 'var(--surf-raised)' }}>
                        <span className="block h-2 rounded-full" style={{ width: `${(d.value / maxDriver) * 100}%`, background: SEVERITY[d.k].fg }} />
                      </span>
                      <span className="w-8 text-right font-mono text-[13px]" style={{ color: 'var(--ink-text)' }}>{d.value}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Panel>

          {/* Secondary stats */}
          <Panel className="!p-2 md:!px-6">
            <div className="flex items-center justify-between gap-3 border-b py-4" style={{ borderColor: 'var(--surf-line)' }}>
              <div className="flex flex-col gap-1">
                <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>TOTAL ASSETS</span>
                <span className="font-heading text-[28px] font-semibold leading-tight" style={{ color: 'var(--ink-text)' }}>{stats.assets}</span>
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{trendDelta(history.assets) >= 0 ? '+' : ''}{trendDelta(history.assets)} this session</span>
              </div>
              <svg width="112" height="36" viewBox="0 0 112 36" aria-hidden="true">
                <polyline points={sparklinePoints(history.assets)} fill="none" stroke="var(--ink-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex items-center justify-between gap-3 border-b py-4" style={{ borderColor: 'var(--surf-line)' }}>
              <div className="flex flex-col gap-1">
                <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>SECURITY EVENTS</span>
                <span className="font-heading text-[28px] font-semibold leading-tight" style={{ color: 'var(--ink-text)' }}>{stats.events}</span>
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{trendDelta(history.events) >= 0 ? '+' : ''}{trendDelta(history.events)} this session</span>
              </div>
              <svg width="112" height="36" viewBox="0 0 112 36" aria-hidden="true">
                <polyline points={sparklinePoints(history.events)} fill="none" stroke="var(--ink-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex flex-col justify-center gap-2 py-4">
              <div className="flex items-end justify-between">
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>AGENTS ONLINE</span>
                  <span className="font-heading text-[28px] font-semibold leading-tight" style={{ color: 'var(--ink-text)' }}>
                    {agentHealth.active} <span className="text-base font-medium" style={{ color: 'var(--ink-faint)' }}>/ {agentHealth.registered}</span>
                  </span>
                </div>
                <button onClick={() => navigate('/agents')} className="text-xs py-1.5 hover:opacity-80" style={{ color: 'var(--ink-link)' }}>
                  {Math.max(0, agentHealth.registered - agentHealth.active)} offline
                </button>
              </div>
              <span className="h-2 rounded-full flex" style={{ background: 'var(--surf-raised)' }}>
                <span className="h-2 rounded-full" style={{ width: `${agentHealth.registered ? Math.round((agentHealth.active / agentHealth.registered) * 100) : 0}%`, background: SEVERITY.good.fg }} />
              </span>
            </div>
          </Panel>

          {/* Needs Attention */}
          <Panel span={2}>
            <Kicker action={<Link_ to="/security-alerts" navigate={navigate}>View all alerts</Link_>}>
              <span className="flex items-baseline gap-3">
                NEEDS ATTENTION
                <span className="font-sans normal-case text-xs font-normal tracking-normal" style={{ color: 'var(--ink-faint)' }}>Ranked by severity, then time</span>
              </span>
            </Kicker>
            <div className="flex flex-col gap-2">
              {needsAttention.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-sm" style={{ color: 'var(--ink-faint)' }}>Nothing needs attention right now</div>
              ) : (
                needsAttention.map(item => (
                  <button
                    key={item.key}
                    onClick={item.onClick}
                    className="h-[60px] flex items-center gap-4 px-4 rounded-[10px] border text-left"
                    style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}
                  >
                    <span className="w-[86px] shrink-0"><SeverityChip level={item.k} /></span>
                    <span className="flex-grow min-w-0 flex flex-col gap-0.5">
                      <span className="text-sm font-medium truncate" style={{ color: 'var(--ink-text)' }}>{item.title}</span>
                      <span className="font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{item.meta}</span>
                    </span>
                    <span className="font-mono text-[11.5px] shrink-0" style={{ color: 'var(--ink-faint)' }}>{timeAgo(item.ts)}</span>
                    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'var(--ink-faint)' }} />
                  </button>
                ))
              )}
            </div>
          </Panel>

          {/* Asset Risk */}
          <Panel>
            <div className="flex flex-col gap-1.5 mb-5">
              <h2 className="font-mono text-[11.5px] font-medium tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>ASSET RISK</h2>
              <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>{stats.assets} assets by risk band</span>
            </div>
            <div className="flex flex-col gap-4.5">
              {bands.map(b => (
                <div key={b.k} className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-[2px] shrink-0" style={{ background: SEVERITY[b.k].fg }} />
                    <span className="flex-grow" style={{ color: 'var(--ink-text)' }}>{b.label}</span>
                    <span className="font-mono text-[13px]" style={{ color: 'var(--ink-text)' }}>{b.value}</span>
                  </div>
                  <span className="h-2 rounded-full flex" style={{ background: 'var(--surf-raised)' }}>
                    <span className="h-2 rounded-full min-w-[8px]" style={{ width: `${(b.value / maxBand) * 100}%`, background: SEVERITY[b.k].fg }} />
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-auto pt-4">
              <Link_ to="/network-map" navigate={navigate}>Open network map</Link_>
            </div>
          </Panel>

          {/* System Status */}
          <Panel>
            <h2 className="font-mono text-[11.5px] font-medium tracking-[0.12em] mb-5" style={{ color: 'var(--ink-faint)' }}>SYSTEM STATUS</h2>
            <div className="grid grid-cols-2 gap-x-4 gap-y-4.5">
              {status.map(s => (
                <div key={s.label} className="flex flex-col gap-1">
                  <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{s.label}</span>
                  <span className="flex items-center gap-1.5 font-mono text-[15px] font-medium" style={{ color: 'var(--ink-text)' }}>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.dot }} />
                    {s.value}
                  </span>
                </div>
              ))}
            </div>
          </Panel>

          {/* System Changes */}
          <Panel span={2}>
            <Kicker action={<Link_ to="/assets" navigate={navigate}>View change log</Link_>}>
              <span className="flex items-baseline gap-3">
                SYSTEM CHANGES
                <span className="font-mono normal-case text-[11.5px] font-normal tracking-normal" style={{ color: 'var(--ink-faint)' }}>{selectedRange.toUpperCase()}</span>
              </span>
            </Kicker>
            <div className="flex flex-col">
              {visibleChanges.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-sm" style={{ color: 'var(--ink-faint)' }}>No changes in this window</div>
              ) : (
                visibleChanges.map((c, i) => (
                  <div
                    key={i}
                    className="grid gap-4 items-center h-[38px] border-b font-mono text-[12.5px]"
                    style={{ gridTemplateColumns: '64px 150px 170px minmax(0, 1fr)', borderColor: 'var(--surf-line)' }}
                  >
                    <span style={{ color: 'var(--ink-faint)' }}>{c.time}</span>
                    <span style={{ color: 'var(--ink-text)' }}>{c.host}</span>
                    <span style={{ color: 'var(--ink-muted)' }}>{c.kind}</span>
                    <span className="truncate" style={{ color: 'var(--ink-muted)' }}>{c.detail}</span>
                  </div>
                ))
              )}
            </div>
          </Panel>
        </div>
    </div>
  );
};

export default Dashboard;
